'use client';

import { useEffect, useRef, useState } from 'react';
import { nb } from '@/lib/nobreak';
import { Arrow, CalIcon, slugify } from './blog-shared';

/** Thin violet reading-progress bar at the top edge (transform only, rAF-throttled). */
export function ReadingBar({ targetId }: { targetId: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const bar = ref.current;
    const art = document.getElementById(targetId);
    if (!bar || !art) return;
    let raf = 0;
    const tick = () => {
      raf = 0;
      const r = art.getBoundingClientRect();
      const total = r.height - window.innerHeight * 0.6;
      const p = total > 0 ? Math.min(1, Math.max(0, -r.top / total)) : 0;
      bar.style.transform = `scaleX(${p})`;
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(tick); };
    tick();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on); window.removeEventListener('resize', on); if (raf) cancelAnimationFrame(raf); };
  }, [targetId]);
  return <div className="bp-prog" ref={ref} aria-hidden="true" />;
}

export type TocItem = { id: string; text: string; level: 2 | 3 };
type Item = TocItem;

/** Table of contents built from the REAL h2/h3 in the rendered article. `variant`: side (sticky, >=1280px) or fold (details, below). */
export function Toc({ targetId, variant, initial = [] }: { targetId: string; variant: 'side' | 'fold'; initial?: TocItem[] }) {
  const [items, setItems] = useState<Item[]>(initial);
  const [active, setActive] = useState('');
  useEffect(() => {
    const art = document.getElementById(targetId);
    if (!art) return;
    const seen = new Set<string>();
    const list: Item[] = [];
    art.querySelectorAll<HTMLHeadingElement>('h2, h3').forEach((h) => {
      const text = (h.textContent || '').trim();
      if (!text) return;
      let id = h.id || slugify(text) || 'section';
      let n = 2;
      const base = id;
      while (seen.has(id) || (document.getElementById(id) && document.getElementById(id) !== h)) id = `${base}-${n++}`;
      h.id = id;
      seen.add(id);
      list.push({ id, text, level: h.tagName === 'H2' ? 2 : 3 });
    });
    setItems(list);
    const heads = list.map((i) => document.getElementById(i.id)!).filter(Boolean);
    let raf = 0;
    const pick = () => {
      raf = 0;
      let cur = heads[0]?.id || '';
      for (const h of heads) { if (h.getBoundingClientRect().top <= 140) cur = h.id; else break; }
      setActive(cur);
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(pick); };
    pick();
    window.addEventListener('scroll', on, { passive: true });
    return () => { window.removeEventListener('scroll', on); if (raf) cancelAnimationFrame(raf); };
  }, [targetId]);

  if (items.length < 3) return null;
  const list = (
    <ul className="bp-toc-list">
      {items.map((i) => (
        <li key={i.id} data-l={i.level}>
          <a href={`#${i.id}`} data-on={active === i.id ? '1' : '0'} aria-current={active === i.id ? 'location' : undefined}>{i.text}</a>
        </li>
      ))}
    </ul>
  );
  if (variant === 'fold') {
    return (
      <details className="bp-toc-det">
        <summary>On this page</summary>
        {list}
      </details>
    );
  }
  return (
    <nav className="bp-toc-side" aria-label="On this page">
      <p className="bp-toc-t">On this page</p>
      {list}
    </nav>
  );
}

export function ShareRow({ url, title }: { url: string; title: string }) {
  const [done, setDone] = useState(false);
  const enc = encodeURIComponent;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setDone(true); setTimeout(() => setDone(false), 2000); } catch { /* clipboard blocked */ }
  };
  const ic = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.9, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, viewBox: '0 0 24 24' };
  return (
    <div className="bp-share">
      <span className="bp-share-l">Share</span>
      <a className="bp-sh" target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" href={`https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`}>
        <svg {...ic}><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6zM2 9h4v12H2zM4 2a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" /></svg>
      </a>
      <a className="bp-sh" target="_blank" rel="noopener noreferrer" aria-label="Share on X" href={`https://twitter.com/intent/tweet?text=${enc(title)}&url=${enc(url)}`}>
        <svg {...ic}><path d="M4 4l16 16M20 4L4 20" /></svg>
      </a>
      <a className="bp-sh" target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`}>
        <svg {...ic}><path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" /></svg>
      </a>
      <button type="button" className="bp-sh" data-done={done ? '1' : '0'} onClick={copy} aria-label={done ? 'Link copied' : 'Copy link'}>
        {done ? <svg {...ic}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg> : <svg {...ic}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1-1" /></svg>}
      </button>
    </div>
  );
}

/** Rail strategy-call card (same recipe as the guides' rail): the ONE lime action while reading; fades out (opacity/transform only) when a closing band is in view. */
export function RailCta({ line, href }: { line: string; href: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let raf = 0;
    const tick = () => {
      raf = 0;
      const band = document.querySelector('.bp-close');
      const hide = !!band && band.getBoundingClientRect().top < window.innerHeight - 60;
      el.setAttribute('data-hide', hide ? '1' : '0');
    };
    const on = () => { if (!raf) raf = requestAnimationFrame(tick); };
    tick();
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on);
    return () => { window.removeEventListener('scroll', on); window.removeEventListener('resize', on); if (raf) cancelAnimationFrame(raf); };
  }, []);
  return (
    <div className="bp-rail-cta" ref={ref} data-hide="0">
      <div className="bp-rc-row"><span className="bp-rc-ico"><CalIcon /></span><p className="bp-rc-txt">{nb(line)}</p></div>
      <a className="bp-rc-btn" href={href} data-cta="blog-rail"><span>Book a Strategy Call</span><Arrow /></a>
    </div>
  );
}
